import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import NavBar from '../components/NavBar';

const PLANS = [
  {
    key: 'family_monthly',
    title: 'Family Monthly',
    price: '$9.99',
    cadence: '/month',
    highlight: '7-day free trial',
    subtext: 'Cancel anytime during your trial and pay nothing.',
    perks: [
      'Full family dashboard for every linked loved one',
      "See today's daily care log the moment your caregiver signs it",
      '7-day trend charts (vitals, mood, meals, sleep)',
      'Instant alerts on falls, incidents, and urgent concerns',
      'Unlimited magic-link share codes for extended family',
      'Cancel anytime',
    ],
    accent: '#2c5364',
    badge: 'Most flexible',
  },
  {
    key: 'family_annual',
    title: 'Family Annual',
    price: '$99',
    cadence: '/year',
    highlight: 'Save $20 vs. monthly',
    subtext: 'Billed once a year. Cancel anytime — no long-term commitment.',
    perks: [
      'Everything in Monthly',
      'Two months free vs. paying monthly',
      'Priority email support',
      "Early access to new features (printable reports, deeper trend charts)",
      'Great for long-term caregiving journeys',
    ],
    accent: '#7c3aed',
    badge: 'Best value',
  },
];

const Plans = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [entitlement, setEntitlement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/user/entitlement`);
        setEntitlement(res.data);
      } catch { /* fine */ }
      finally { setLoading(false); }
    })();
  }, []);

  const startCheckout = async (lookupKey) => {
    setCheckoutLoading(lookupKey);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(null);
    }
  };

  if (loading) return <div className="loading-screen">Loading plans…</div>;

  return (
    <div className="dashboard-container" data-testid="plans-page">
      <NavBar onLogout={onLogout} />
      <div className="dashboard-content page-container">
        <div className="plans-hero">
          <h1><i className="fa-solid fa-people-roof" style={{ marginRight: 10, color: '#2c5364' }} /> Bring Your Family Into the Care</h1>
          <p>Give family members read-only access to <strong>{user?.name?.split(' ')[0] || 'their loved one'}'s</strong> daily care log, mood, meals, vitals, and alerts — so they always know how their loved one is doing without having to call.</p>
        </div>

        {entitlement?.entitled && (
          <div className="entitlement-banner" data-testid="entitlement-active">
            <i className="fa-solid fa-circle-check" />
            <div>
              <strong>You're already subscribed.</strong> Plan: <strong>{entitlement.subscription_plan === 'annual' ? 'Family Annual' : entitlement.subscription_plan === 'lifetime' ? 'Family Lifetime' : 'Family Monthly'}</strong>
              <div style={{ fontSize: '0.85rem', opacity: 0.85, marginTop: 4 }}>
                Head to your Family Dashboard to view care logs.
              </div>
            </div>
            <button className="submit-button" style={{ width: 'auto', padding: '0.55rem 1.1rem' }} onClick={() => navigate('/family/dashboard')} data-testid="go-family-dashboard">
              Family Dashboard <i className="fa-solid fa-arrow-right" />
            </button>
          </div>
        )}

        <div className="plans-grid" data-testid="plans-grid">
          {PLANS.map(plan => (
            <div key={plan.key} className="plan-card" data-testid={`plan-card-${plan.key}`} style={{ borderTopColor: plan.accent }}>
              <div className="plan-badge" style={{ background: plan.accent }}>{plan.badge}</div>
              <h2 className="plan-title">{plan.title}</h2>
              <div className="plan-price">
                <span className="plan-amount">{plan.price}</span>
                <span className="plan-cadence">{plan.cadence}</span>
              </div>
              <div className="plan-highlight" style={{ color: plan.accent }}>
                <i className="fa-solid fa-star" /> {plan.highlight}
              </div>
              <p className="plan-subtext">{plan.subtext}</p>
              <ul className="plan-perks">
                {plan.perks.map((p, i) => (
                  <li key={i}><i className="fa-solid fa-check" style={{ color: plan.accent }} /> {p}</li>
                ))}
              </ul>
              <button
                className="plan-cta-btn"
                style={{ background: plan.accent }}
                onClick={() => startCheckout(plan.key)}
                disabled={checkoutLoading === plan.key || entitlement?.entitled}
                data-testid={`plan-cta-${plan.key}`}
              >
                {entitlement?.entitled
                  ? 'Already subscribed'
                  : checkoutLoading === plan.key
                    ? 'Redirecting…'
                    : plan.key === 'family_monthly' ? 'Start 7-Day Free Trial' : 'Start Annual Plan'}
                {!entitlement?.entitled && checkoutLoading !== plan.key && <i className="fa-solid fa-arrow-right" style={{ marginLeft: 8 }} />}
              </button>
            </div>
          ))}
        </div>

        <div className="plans-footnote">
          <i className="fa-solid fa-shield-halved" /> Payments are processed securely by Stripe. Test mode: use card <code>4242 4242 4242 4242</code> · any future expiry · any CVC.
        </div>
      </div>
    </div>
  );
};

export default Plans;
